import { Badge, Button } from "react-bootstrap";
import { FaRedo, FaRegFileAlt } from "react-icons/fa";

import * as api from "../../api/api";
import * as missionApi from "../../api/missionApi";
import { useUser } from "../../contexts/user_provider";
import StateIcon from "../StateIcon";

function appCanRestart(apps, appId) {
    const matchingApp = apps.find((appItem) => appItem.id === appId);
    const state = matchingApp?.state;
    if (state) {
        return [
            missionApi.STATE_CANCELLED,
            missionApi.STATE_FAILED,
            missionApi.STATE_SUCCEEDED,
        ].includes(matchingApp?.state);
    }
    return true;
}

function getAppLogUrl(apps, appId) {
    const matchingApp = apps.find((appItem) => appItem.id === appId);
    const batchJobIds = matchingApp?.batchJobIds || [];
    if (batchJobIds.length > 0) {
        return `https://console.cloud.google.com/batch/jobsDetail/regions/europe-west1/jobs/${batchJobIds.at(-1)}/logs`;
    }
    return null;
}

export default function AppStatusMessage({
    mission,
    app,
    allApps,
    missionHandlerStarted,
    setMissionHandlerStarted,
}) {
    const { getUserIsAdmin } = useUser();

    const dependencyApps = missionApi
        .getMissionApps(mission)
        .filter((bgApp) => bgApp?.type === "background")
        .filter((bgApp) => bgApp?.rootApp === app?.id)
        .map((bgApp) => ({
            ...bgApp,
            fullApp: allApps.find((a) => a.id === bgApp.id) || {},
        }))
        .sort((a, b) => {
            const nameA = a.fullApp.name?.toLowerCase() || "";
            const nameB = b.fullApp.name?.toLowerCase() || "";

            // Prioritize "export" first, then "call"
            if (nameA.includes("export") && !nameB.includes("export"))
                return -1;
            if (!nameA.includes("export") && nameB.includes("export")) return 1;
            if (nameA.includes("call") && !nameB.includes("call")) return -1;
            if (!nameA.includes("call") && nameB.includes("call")) return 1;

            return 0; // Otherwise, keep the original order
        });

    const handleAppRestart = async (e, appId) => {
        e.stopPropagation();
        try {
            await api.setAppPending(
                mission.organizationId,
                mission.locationId,
                mission.id,
                appId,
            );
            if (!missionHandlerStarted) {
                await api.restartMissionHandler(
                    mission.organizationId,
                    mission.locationId,
                    mission.id,
                    {
                        restart: true,
                        onlyApps: true,
                    },
                );
                setMissionHandlerStarted(true);
            }
        } catch (error) {
            console.error("Error restarting app:", error);
        }
    };

    const appLogUrl = getAppLogUrl(mission.apps, app?.id);

    return (
        <div className="mt-1">
            {/* First row: Icon and App Name */}
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    <StateIcon
                        state={missionApi.getApplicationState(mission, app)}
                        className="me-3"
                    />
                    <span className="ms-2" style={{ marginTop: "-2px" }}>
                        {app?.name || "Unknown app"}
                    </span>
                </div>
                {getUserIsAdmin() && (
                    <div className="d-flex gap-1">
                        {appLogUrl && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => window.open(appLogUrl, "_blank")}
                            >
                                <FaRegFileAlt /> Logs
                            </Button>
                        )}

                        {appCanRestart(mission.apps, app?.id) && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                    handleAppRestart(e, app.id);
                                }}
                            >
                                Restart
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Second row: Badges (aligned with app name) */}
            {getUserIsAdmin() && dependencyApps.length > 0 && (
                <>
                    <div className="ms-4">
                        <small className="text-muted ms-3">Depends on:</small>
                    </div>
                    <div className="d-flex flex-wrap ms-4 ps-2">
                        {dependencyApps.map((dependencyApp) => {
                            const dependencyAppLogUrl = getAppLogUrl(
                                mission.apps,
                                dependencyApp.id,
                            );
                            return (
                                <Badge
                                    key={dependencyApp.id}
                                    className={`ms-2 mb-1 rounded-pill border border-secondary text-secondary bg-transparent fw-light`}
                                    style={{
                                        padding: "4px 8px",
                                        fontSize: "0.85rem",
                                        cursor: "default",
                                    }}
                                >
                                    <StateIcon
                                        state={dependencyApp.state}
                                        className="me-1"
                                    />
                                    {dependencyApp.fullApp?.name ||
                                        "Unknown app"}
                                    {dependencyAppLogUrl && (
                                        <Button
                                            style={{
                                                padding: "0.08rem 0.25rem",
                                                fontSize: "0.7rem",
                                                lineHeight: "1rem",
                                            }}
                                            className="ms-1"
                                            variant="outline-secondary"
                                            onClick={() =>
                                                window.open(
                                                    dependencyAppLogUrl,
                                                    "_blank",
                                                )
                                            }
                                            title="Logs"
                                        >
                                            <FaRegFileAlt
                                                style={{ marginTop: "-2px" }}
                                            />
                                        </Button>
                                    )}
                                    {appCanRestart(
                                        mission.apps,
                                        dependencyApp?.id,
                                    ) && (
                                        <Button
                                            style={{
                                                padding: "0.08rem 0.25rem",
                                                fontSize: "0.7rem",
                                                lineHeight: "1rem",
                                            }}
                                            className="ms-1"
                                            variant="outline-primary"
                                            onClick={(e) => {
                                                handleAppRestart(
                                                    e,
                                                    dependencyApp.id,
                                                );
                                            }}
                                            title="Restart"
                                        >
                                            <FaRedo
                                                style={{ marginTop: "-2px" }}
                                            />
                                        </Button>
                                    )}
                                </Badge>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
